import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
})

// Inject access token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token')
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

// Auto-refresh on 401
let refreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

api.interceptors.response.use(
  res => res,
  async (err: AxiosError) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (err.response?.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true
      if (!refreshing) {
        refreshing = true
        try {
          const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
          const newToken = data.accessToken
          localStorage.setItem('access_token', newToken)
          refreshQueue.forEach(({ resolve }) => resolve(newToken))
          refreshQueue = []
          return api(original)
        } catch (refreshErr: any) {
          // Only force logout if the refresh endpoint itself returned 401/403
          // (bad/expired refresh token). Network errors (503, timeout) should
          // not clear the session — the user is still authenticated, the server
          // was just temporarily unavailable.
          const refreshStatus = refreshErr?.response?.status
          if (refreshStatus === 401 || refreshStatus === 403) {
            localStorage.removeItem('access_token')
            refreshQueue.forEach(({ reject }) => reject(refreshErr))
            refreshQueue = []
            window.location.href = '/login'
          } else {
            // Transient failure (503, network) — reject queued requests so
            // they don't hang, but keep the session alive
            refreshQueue.forEach(({ reject }) => reject(refreshErr))
            refreshQueue = []
          }
          return Promise.reject(err)
        } finally {
          refreshing = false
        }
      }
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            original.headers['Authorization'] = `Bearer ${token}`
            resolve(api(original))
          },
          reject,
        })
      })
    }
    return Promise.reject(err)
  },
)

export default api
